import { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, updateDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db, addAuditLog } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import styles from './TrackingPage.module.css';
import { formatOrderId } from '../../utils/formatOrderId';

const STATUS_OPTIONS = ['Pending', 'In Transit', 'Delivered'];
const normalizeStatus = s => s === 'On its way' ? 'In Transit' : s;

function StatusBadge({ status }) {
  const cls = styles[`badge_${status.replace(/\s+/g, '_')}`];
  return <span className={`${styles.badge} ${cls ?? ''}`}>{status}</span>;
}

function StatCard({ label, value, accent }) {
  return (
    <div className={styles.statCard}>
      <span className={`${styles.statValue} ${accent ? styles.statAccent : ''}`}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
    </div>
  );
}

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-PH', { hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function TrackingPage() {
  const { user }                          = useAuth();
  const [deliveries, setDeliveries]       = useState([]);
  const [pendingUpdates, setPendingUpdates] = useState({});
  const [saving, setSaving]               = useState({});

  useEffect(() => {
    const q = query(collection(db, 'orders'), orderBy('updatedAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setDeliveries(snap.docs.map(d => {
        const data = d.data();
        return { _docId: d.id, ...data, status: normalizeStatus(data.status) };
      }));
    });
    return unsub;
  }, []);

  function handleSelectChange(id, newStatus) {
    setPendingUpdates(prev => ({ ...prev, [id]: newStatus }));
  }

  async function applyUpdate(delivery) {
    const newStatus = pendingUpdates[delivery._docId];
    if (!newStatus || saving[delivery._docId]) return;

    const eta =
      newStatus === 'Delivered'  ? 'Delivered' :
      newStatus === 'In Transit' ? 'Est. arrival: 2–4 hrs' :
      'Awaiting dispatch';

    setSaving(prev => ({ ...prev, [delivery._docId]: true }));
    try {
      await updateDoc(doc(db, 'orders', delivery._docId), {
        status:           newStatus,
        estimatedArrival: eta,
        updatedAt:        serverTimestamp(),
      });
      await addAuditLog(user.uid, user.name, `Updated delivery ${delivery._docId} to "${newStatus}"`, 'Tracking');
      setPendingUpdates(prev => { const n = { ...prev }; delete n[delivery._docId]; return n; });
    } catch (e) {
      console.error('Update tracking failed:', e);
    } finally {
      setSaving(prev => ({ ...prev, [delivery._docId]: false }));
    }
  }

  const totalActive    = deliveries.filter(d => d.status !== 'Delivered').length;
  const inTransitCount = deliveries.filter(d => d.status === 'In Transit').length;
  const pendingCount   = deliveries.filter(d => d.status === 'Pending').length;
  const deliveredCount = deliveries.filter(d => d.status === 'Delivered').length;

  return (
    <div className={styles.page}>
      <PageHeader title="Delivery Management" description="Monitor all active deliveries and shipment progress" />

      <div className={styles.statRow}>
        <StatCard label="Total Active Deliveries" value={totalActive} />
        <StatCard label="In Transit"               value={inTransitCount} accent />
        <StatCard label="Pending Dispatch"        value={pendingCount} />
        <StatCard label="Delivered"               value={deliveredCount} />
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Order ID</th><th>Branch</th><th>Status</th><th>ETA</th><th>Last Updated</th><th>Action</th>
            </tr>
          </thead>
          <tbody>
            {deliveries.length === 0 ? (
              <tr><td colSpan={6} className={styles.empty}>No deliveries found.</td></tr>
            ) : (
              deliveries.map((d, idx) => (
                <tr key={d._docId} className={idx % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                  <td className={styles.orderId}>{formatOrderId(d._docId)}</td>
                  <td className={styles.branch}>{d.branch ?? d.branchId ?? '—'}</td>
                  <td><StatusBadge status={d.status} /></td>
                  <td className={styles.eta}>{d.estimatedArrival || '—'}</td>
                  <td className={styles.dispatch}>{formatDate(d.updatedAt)}</td>
                  <td>
                    <div className={styles.actionCell}>
                      <select
                        className={styles.statusSelect}
                        value={pendingUpdates[d._docId] ?? d.status}
                        onChange={e => handleSelectChange(d._docId, e.target.value)}
                      >
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button
                        className={styles.updateBtn}
                        onClick={() => applyUpdate(d)}
                        disabled={!pendingUpdates[d._docId] || pendingUpdates[d._docId] === d.status || saving[d._docId]}
                      >
                        {saving[d._docId] ? '…' : 'Update'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
